import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, UserPlus, RotateCw } from "lucide-react";
import ProfessionalSearch, { ProfessionalFoundCard } from "@/components/ProfessionalSearch";

const EMP_TYPES = [
  "Full Time", "Part Time", "Contract", "Temporary", "Internship",
  "Apprenticeship", "Volunteer", "Freelance", "Project Based",
];
const EMP_STATUS = [
  "Active", "Probation", "On Leave", "Resigned", "Terminated", "Absconded", "Retired",
];
const EXIT_STATUSES = new Set(["Resigned", "Terminated", "Absconded", "Retired"]);

const empty = {
  full_name: "", personal_email: "", phone: "", employee_id: "",
  designation: "", department: "", employment_type: "Full Time",
  joining_date: "", work_location: "", employment_status: "Active",
  reporting_manager: "", branch: "",
  job_end_date: "", exit_reason: "",
  official_email: "", date_of_birth: "", pan_number: "", emergency_contact: "",
};

export default function AddEmployeePage() {
  const nav = useNavigate();
  // step: "search" | "form"
  const [step, setStep] = useState("search");
  const [linkedPro, setLinkedPro] = useState(null);
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);

  const isExitStatus = EXIT_STATUSES.has(f.employment_status);
  const prefilledFromPassport = !!linkedPro;

  const startWithLinked = (pro) => {
    setLinkedPro(pro);
    setF({
      ...empty,
      full_name: pro.full_name || "",
      personal_email: pro.email || "",
      phone: pro.phone || "",
    });
    setStep("form");
  };

  const startBlank = () => {
    setLinkedPro(null);
    setF(empty);
    setStep("form");
  };

  const resetSearch = () => {
    setLinkedPro(null);
    setF(empty);
    setStep("search");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isExitStatus) {
      if (!f.job_end_date) { toast.error("Job End Date is required for this status."); return; }
      if (!f.exit_reason.trim()) { toast.error("Exit Reason is required for this status."); return; }
    }
    setLoading(true);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      // Re-require non-null on required fields
      payload.full_name = f.full_name;
      payload.personal_email = f.personal_email;
      payload.phone = f.phone;
      payload.employee_id = f.employee_id;
      payload.designation = f.designation;
      payload.department = f.department;
      payload.employment_type = f.employment_type;
      payload.joining_date = f.joining_date;
      payload.work_location = f.work_location;
      payload.employment_status = f.employment_status;
      const { data } = await api.post("/records/employees", payload);
      if (data.status === "Linked") {
        toast.success("Employee added and linked to existing EthicCred passport.");
      } else {
        toast.success("Employee added. Awaiting individual to claim.");
      }
      nav("/workspace/employees");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceLayout subtitle="HR workspace" title="Add Employee">
      {step === "search" && (
        <ProfessionalSearch
          onFound={startWithLinked}
          onCancel={startBlank}
          primaryLabel="Add as Employee"
        />
      )}

      {step === "form" && (
        <>
          {linkedPro && (
            <div className="mb-5">
              <ProfessionalFoundCard
                pro={linkedPro}
                primaryLabel="Continue with this profile"
                primaryTestId="form-continue-linked"
                onPrimary={() => {}}
                onCancel={resetSearch}
              />
            </div>
          )}
          {!linkedPro && (
            <div className="mb-5 flex items-center gap-2 text-xs text-ec-ink-soft" data-testid="form-back-to-search">
              <button type="button" onClick={resetSearch} className="underline hover:text-ec-ink flex items-center gap-1">
                <RotateCw className="w-3 h-3" /> Search for an existing EthicCred passport instead
              </button>
            </div>
          )}

          <Card className="rounded-sm border-ec-line">
            <CardContent className="p-6">
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="add-employee-form">
                {!prefilledFromPassport && (
                  <>
                    <SectionTitle>Personal information</SectionTitle>
                    <Field label="Full Name" required>
                      <Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required data-testid="emp-name" />
                    </Field>
                    <Field label="Personal Email" required>
                      <Input type="email" value={f.personal_email} onChange={(e) => setF({ ...f, personal_email: e.target.value })} required data-testid="emp-email" />
                    </Field>
                    <Field label="Phone Number" required>
                      <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required data-testid="emp-phone" />
                    </Field>
                    <Field label="Date of Birth">
                      <Input type="date" value={f.date_of_birth} onChange={(e) => setF({ ...f, date_of_birth: e.target.value })} data-testid="emp-dob" />
                    </Field>
                  </>
                )}

                <SectionTitle>{prefilledFromPassport ? "Employment information (only field needed)" : "Employment information"}</SectionTitle>
                <Field label="Employee ID" required>
                  <Input value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} required data-testid="emp-id" />
                </Field>
                <Field label="Designation" required>
                  <Input value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} required data-testid="emp-designation" />
                </Field>
                <Field label="Department" required>
                  <Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} required data-testid="emp-department" />
                </Field>
                <Field label="Employment Type" required>
                  <Select value={f.employment_type} onValueChange={(v) => setF({ ...f, employment_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="emp-type-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Joining Date" required>
                  <Input type="date" value={f.joining_date} onChange={(e) => setF({ ...f, joining_date: e.target.value })} required data-testid="emp-joining" />
                </Field>
                <Field label="Reporting Manager">
                  <Input value={f.reporting_manager} onChange={(e) => setF({ ...f, reporting_manager: e.target.value })} placeholder="e.g. Priya Mehta" data-testid="emp-reporting-manager" />
                </Field>
                <Field label="Branch">
                  <Input value={f.branch} onChange={(e) => setF({ ...f, branch: e.target.value })} placeholder="e.g. Bangalore HQ" data-testid="emp-branch" />
                </Field>
                <Field label="Work Location" required>
                  <Input value={f.work_location} onChange={(e) => setF({ ...f, work_location: e.target.value })} required data-testid="emp-location" />
                </Field>
                <Field label="Employment Status" required>
                  <Select value={f.employment_status} onValueChange={(v) => setF({ ...f, employment_status: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="emp-status-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMP_STATUS.map((t) => <SelectItem key={t} value={t} data-testid={`emp-status-opt-${t}`}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>

                {isExitStatus && (
                  <>
                    <div className="sm:col-span-2 border-t border-ec-line pt-5">
                      <div className="eyebrow text-ec-error mb-2">Exit details (required for {f.employment_status})</div>
                    </div>
                    <Field label="Job End Date" required>
                      <Input type="date" value={f.job_end_date} onChange={(e) => setF({ ...f, job_end_date: e.target.value })} required data-testid="emp-job-end-date" />
                    </Field>
                    <Field label="Exit Reason" required>
                      <Input value={f.exit_reason} onChange={(e) => setF({ ...f, exit_reason: e.target.value })} required placeholder="e.g. Career growth, redundancy, retirement…" data-testid="emp-exit-reason" />
                    </Field>
                  </>
                )}

                <div className="sm:col-span-2 border-t border-ec-line pt-5">
                  <div className="eyebrow mb-3">Optional fields</div>
                </div>
                <Field label="Official Company Email">
                  <Input type="email" value={f.official_email} onChange={(e) => setF({ ...f, official_email: e.target.value })} data-testid="emp-official-email" />
                </Field>
                {prefilledFromPassport && (
                  <Field label="Date of Birth">
                    <Input type="date" value={f.date_of_birth} onChange={(e) => setF({ ...f, date_of_birth: e.target.value })} data-testid="emp-dob" />
                  </Field>
                )}
                <Field label="PAN Number" hint="Optional · Encrypted · Masked (ABCDE****F)">
                  <Input value={f.pan_number} onChange={(e) => setF({ ...f, pan_number: e.target.value.toUpperCase() })} maxLength={10} placeholder="ABCDE1234F" data-testid="emp-pan" />
                </Field>
                <Field label="Emergency Contact">
                  <Input value={f.emergency_contact} onChange={(e) => setF({ ...f, emergency_contact: e.target.value })} data-testid="emp-emergency" />
                </Field>

                <div className="sm:col-span-2 flex justify-end gap-2 border-t border-ec-line pt-5">
                  <Button type="button" variant="outline" className="rounded-sm" onClick={() => nav("/workspace/employees")} data-testid="emp-cancel">Cancel</Button>
                  <Button type="submit" disabled={loading} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="emp-submit">
                    {prefilledFromPassport ? <UserPlus className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                    {loading ? "Saving…" : (prefilledFromPassport ? "Add Employee" : "Create Record")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </WorkspaceLayout>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="sm:col-span-2">
      <div className="eyebrow text-ec-ink-soft">{children}</div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <Label className="eyebrow">{label} {required && <span className="text-ec-emerald">*</span>}</Label>
      <div className="mt-2">{children}</div>
      {hint && <p className="text-[11px] text-ec-ink-soft mt-1 font-mono-plex">{hint}</p>}
    </div>
  );
}
